import { Helmet } from '@slorber/react-helmet-async';
import { useEffect } from 'react';

/**
 * SEO Component for dynamic per-page metadata
 * 
 * Generates:
 * - Unique <title> with keyword optimization
 * - Meta description (150-160 chars recommended)
 * - OpenGraph tags for Facebook/LinkedIn
 * - Twitter Cards for Twitter/X
 * - Canonical URL
 * - JSON-LD structured data with Google Rich Results compliance:
 *   - datePublished, dateModified
 *   - mainEntityOfPage
 *   - author
 */

const SITE_NAME = 'Zarolla Online Art Academy';
const SITE_URL = 'https://zarolla.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_oilpaint-edu/artifacts/k884xqpf_Zarolla_logo_final_orange_nosub.png';
const TWITTER_HANDLE = '@zarolla';

// Organization schema (reused across pages)
const ORGANIZATION_SCHEMA = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    '@id': `${SITE_URL}/#logo`,
    url: DEFAULT_IMAGE,
    contentUrl: DEFAULT_IMAGE,
    caption: SITE_NAME,
  },
  image: { '@id': `${SITE_URL}/#logo` },
  sameAs: [
    // Add social media profiles when available
  ],
};

// Default author (the organization)
const DEFAULT_AUTHOR = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
};

// Site founding date (used for datePublished on evergreen pages)
const SITE_FOUNDING_DATE = '2007-01-01';

// SEO keyword priorities for title optimization
const KEYWORDS = {
  primary: 'online art classes',
  methods: ['Frank Reilly method', 'alla prima painting', 'classical art training'],
  instructors: ['Glen Orbik drawing', 'Tony Pro painting'],
};

/**
 * Generate optimized page title
 * Format: [Page Title] | [Keyword] | Zarolla
 */
const generateTitle = (pageTitle, keyword = null) => {
  if (keyword && !pageTitle.toLowerCase().includes(keyword.toLowerCase())) {
    return `${pageTitle} | ${keyword} | ${SITE_NAME}`;
  }
  return `${pageTitle} | ${SITE_NAME}`;
};

/**
 * Truncate description to optimal length (150-160 chars)
 */
const truncateDescription = (text, maxLength = 155) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3).trim() + '...';
};

/**
 * Format date to ISO 8601 format for schema.org
 */
const formatDate = (date) => {
  if (!date) return new Date().toISOString().split('T')[0];
  if (typeof date === 'string') {
    // If already ISO format, return date portion
    if (date.includes('T')) return date.split('T')[0];
    return date;
  }
  return date.toISOString().split('T')[0];
};

/**
 * Inject meta tag into document head
 */
const setMetaTag = (name, content, isProperty = false) => {
  if (!content) return;
  const attr = isProperty ? 'property' : 'name';
  let element = document.querySelector(`meta[${attr}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

/**
 * Inject link tag into document head
 */
const setLinkTag = (rel, href) => {
  if (!href) return;
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

/**
 * Inject JSON-LD script into document head
 */
const setJsonLd = (data) => {
  if (!data) return;
  let element = document.querySelector('script[type="application/ld+json"][data-seo="true"]');
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.setAttribute('data-seo', 'true');
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
};

export const SEO = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  keywords = [],
  noindex = false,
  structuredData = null,
  course = null,
  video = null,
  instructor = null,
  // New fields for enhanced structured data
  datePublished = null,
  dateModified = null,
  author = null,
}) => {
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const metaDescription = truncateDescription(description);
  const pageTitle = generateTitle(title, keywords[0]);
  
  // Format dates
  const publishedDate = formatDate(datePublished || SITE_FOUNDING_DATE);
  const modifiedDate = formatDate(dateModified || new Date());
  
  // Determine author schema
  const authorSchema = author 
    ? (typeof author === 'string' 
        ? { '@type': 'Person', name: author }
        : author)
    : DEFAULT_AUTHOR;
  
  // Generate structured data based on content type
  let schemaData = structuredData;
  
  if (course) {
    // Enhanced Course schema with Google Rich Results compliance
    schemaData = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      '@id': `${canonicalUrl}#course`,
      name: course.title,
      description: course.description || metaDescription,
      url: canonicalUrl,
      
      // Required for Google Rich Results
      provider: {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        sameAs: SITE_URL,
      },
      
      // Author/Instructor
      author: course.instructor 
        ? { '@type': 'Person', name: course.instructor }
        : authorSchema,
      
      // Dates
      datePublished: publishedDate,
      dateModified: modifiedDate,
      
      // Main entity reference
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      
      // Course-specific fields
      image: course.thumbnail || image,
      inLanguage: 'en-US',
      
      // Instructor (separate from author for courses)
      instructor: course.instructor ? {
        '@type': 'Person',
        name: course.instructor,
        worksFor: { '@id': `${SITE_URL}/#organization` },
      } : undefined,
      
      // Pricing
      offers: course.cost ? {
        '@type': 'Offer',
        price: course.cost,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: canonicalUrl,
        validFrom: publishedDate,
      } : undefined,
      
      // Course details
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: course.duration || 'Self-paced',
        inLanguage: 'en-US',
      },
      
      // Educational details
      educationalLevel: 'Beginner to Advanced',
      teaches: course.teaches || ['Drawing', 'Painting', 'Art Techniques'],
    };
  } else if (video) {
    // Enhanced VideoObject schema
    schemaData = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      '@id': `${canonicalUrl}#video`,
      name: video.title,
      description: video.description || metaDescription,
      url: canonicalUrl,
      
      // Thumbnail
      thumbnailUrl: video.thumbnail || image,
      
      // Dates - required for Google Video Rich Results
      uploadDate: formatDate(video.created_at || datePublished),
      datePublished: formatDate(video.created_at || datePublished),
      dateModified: modifiedDate,
      
      // Duration in ISO 8601 format
      duration: video.duration 
        ? `PT${Math.floor(video.duration / 60)}M${video.duration % 60}S` 
        : undefined,
      
      // Content URLs
      contentUrl: video.vimeo_id ? `https://vimeo.com/${video.vimeo_id}` : undefined,
      embedUrl: video.vimeo_id ? `https://player.vimeo.com/video/${video.vimeo_id}` : undefined,
      
      // Main entity reference
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      
      // Author
      author: video.instructor 
        ? { '@type': 'Person', name: video.instructor }
        : authorSchema,
      
      // Publisher (required for Video Rich Results)
      publisher: {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        logo: {
          '@type': 'ImageObject',
          url: DEFAULT_IMAGE,
        },
      },
      
      // Additional video metadata
      inLanguage: 'en-US',
      isFamilyFriendly: true,
    };
  } else if (instructor) {
    // Enhanced Person schema for instructors
    schemaData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      '@id': `${canonicalUrl}#person`,
      name: instructor.name,
      description: instructor.bio || metaDescription,
      url: canonicalUrl,
      
      // Image
      image: instructor.image || image,
      
      // Main entity reference
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      
      // Professional details
      jobTitle: 'Art Instructor',
      worksFor: {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
      },
      
      // Areas of expertise
      knowsAbout: instructor.expertise || [
        'Oil Painting',
        'Portrait Painting',
        'Figure Drawing',
        'Classical Art',
      ],
      
      // Social profiles (if available)
      sameAs: instructor.socialLinks || [],
    };
  }
  
  // Use useEffect to directly manipulate document head (more reliable with React 19)
  useEffect(() => {
    // Set document title
    document.title = pageTitle;
    
    // Set basic meta tags
    setMetaTag('description', metaDescription);
    setMetaTag('keywords', keywords.join(', '));
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    
    // Set article meta tags for dates
    setMetaTag('article:published_time', publishedDate, true);
    setMetaTag('article:modified_time', modifiedDate, true);
    
    // Set OpenGraph tags
    setMetaTag('og:type', type, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:title', pageTitle, true);
    setMetaTag('og:description', metaDescription, true);
    setMetaTag('og:image', image, true);
    setMetaTag('og:site_name', SITE_NAME, true);
    setMetaTag('og:updated_time', modifiedDate, true);
    
    // Set Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:url', canonicalUrl);
    setMetaTag('twitter:title', pageTitle);
    setMetaTag('twitter:description', metaDescription);
    setMetaTag('twitter:image', image);
    setMetaTag('twitter:site', TWITTER_HANDLE);
    
    // Set canonical link
    setLinkTag('canonical', canonicalUrl);
    
    // Set JSON-LD structured data
    if (schemaData) {
      setJsonLd(schemaData);
    }
  }, [pageTitle, metaDescription, canonicalUrl, image, type, keywords, noindex, schemaData, publishedDate, modifiedDate]);
  
  // Also use Helmet as backup for SSR compatibility
  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
};

// Pre-configured SEO components for common pages with enhanced structured data

export const HomePageSEO = () => (
  <SEO
    title="Online Art Courses & Painting Videos from Master Artists"
    description="Learn oil painting, portrait drawing, figure drawing, and sculpture from master artists like Tony Pro, Jeremy Lipking, and Glen Orbik. Stream professional art instruction on demand."
    url="/"
    keywords={['online art courses', 'oil painting videos', 'portrait painting course', 'figure drawing lessons', 'classical art training', 'alla prima painting']}
    datePublished="2007-01-01"
    dateModified={new Date().toISOString().split('T')[0]}
    structuredData={{
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: SITE_NAME,
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            '@id': `${SITE_URL}/#logo`,
            url: DEFAULT_IMAGE,
            contentUrl: DEFAULT_IMAGE,
            caption: SITE_NAME,
          },
          description: 'Professional online art education from master artists. Oil painting, portraiture, figure drawing, and sculpture instruction.',
          foundingDate: '2007',
          sameAs: [],
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: SITE_URL,
          name: SITE_NAME,
          description: 'Professional online art education from master artists',
          publisher: { '@id': `${SITE_URL}/#organization` },
          inLanguage: 'en-US',
        },
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/#webpage`,
          url: SITE_URL,
          name: `Online Art Courses & Painting Videos from Master Artists | ${SITE_NAME}`,
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: { '@id': `${SITE_URL}/#organization` },
          datePublished: '2007-01-01',
          dateModified: new Date().toISOString().split('T')[0],
          description: 'Learn oil painting, portrait drawing, figure drawing, and sculpture from master artists. Stream professional art instruction on demand.',
          inLanguage: 'en-US',
          potentialAction: [{
            '@type': 'ReadAction',
            target: [SITE_URL],
          }],
        },
      ],
    }}
  />
);

export const CoursesPageSEO = () => (
  <SEO
    title="Online Art Courses — Portrait, Figure Drawing & Oil Painting"
    description="Multi-session online art courses in portrait drawing, oil painting, figure drawing, and classical techniques. Learn from Tony Pro, Jeremy Lipking, Glen Orbik, and other master artists."
    url="/courses"
    keywords={['online art courses', 'portrait drawing course', 'oil painting course', 'figure drawing course', 'classical art instruction']}
    datePublished="2007-01-01"
    dateModified={new Date().toISOString().split('T')[0]}
    structuredData={{
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/courses#webpage`,
      url: `${SITE_URL}/courses`,
      name: `Online Art Courses — Portrait, Figure Drawing & Oil Painting | ${SITE_NAME}`,
      description: 'Multi-session online art courses in portrait drawing, oil painting, figure drawing, and classical techniques from master artists.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: {
        '@type': 'Thing',
        name: 'Art Education',
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [],
      },
      datePublished: '2007-01-01',
      dateModified: new Date().toISOString().split('T')[0],
      author: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'en-US',
    }}
  />
);

export const WorkshopsPageSEO = () => (
  <SEO
    title="Painting Videos & Workshops — Oil Painting Demonstrations"
    description="Watch master artists paint from start to finish. Alla prima oil painting workshops, portrait demonstrations, and landscape painting sessions by Tony Pro, Jeremy Lipking, and more."
    url="/workshops"
    keywords={['painting videos', 'oil painting demonstrations', 'alla prima painting workshop', 'portrait painting video', 'painting tutorial']}
    datePublished="2007-01-01"
    dateModified={new Date().toISOString().split('T')[0]}
    structuredData={{
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/workshops#webpage`,
      url: `${SITE_URL}/workshops`,
      name: `Painting Videos & Workshops — Oil Painting Demonstrations | ${SITE_NAME}`,
      description: 'Watch master artists paint from start to finish. Alla prima oil painting workshops and portrait demonstrations.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      datePublished: '2007-01-01',
      dateModified: new Date().toISOString().split('T')[0],
      author: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'en-US',
    }}
  />
);

export const SculpturesPageSEO = () => (
  <SEO
    title="Sculpture Videos — Figure Sculpting & Portrait Bust Instruction"
    description="Learn classical figure sculpting, portrait bust modeling, and clay sculpture techniques from expert sculptors. Professional video instruction for artists of all levels."
    url="/sculptures"
    keywords={['sculpture videos', 'figure sculpting instruction', 'portrait bust sculpture', 'clay modeling tutorial', 'classical sculpture techniques']}
    datePublished="2007-01-01"
    dateModified={new Date().toISOString().split('T')[0]}
    structuredData={{
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/sculptures#webpage`,
      url: `${SITE_URL}/sculptures`,
      name: `Sculpture Videos — Figure Sculpting & Portrait Bust Instruction | ${SITE_NAME}`,
      description: 'Learn classical figure sculpting and portrait bust modeling from expert sculptors.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      datePublished: '2007-01-01',
      dateModified: new Date().toISOString().split('T')[0],
      author: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'en-US',
    }}
  />
);

export const AboutPageSEO = () => (
  <SEO
    title="About Zarolla — Classical Art Education from Master Artists"
    description="Zarolla Online Art Academy brings classical art training to students worldwide. Founded on the teaching traditions of master painters and sculptors including Tony Pro, Glen Orbik, and Jeremy Lipking."
    url="/about"
    keywords={['about Zarolla', 'online art academy', 'classical art education', 'master art instructors', 'art school history']}
    datePublished="2007-01-01"
    dateModified={new Date().toISOString().split('T')[0]}
    structuredData={{
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${SITE_URL}/about#webpage`,
      url: `${SITE_URL}/about`,
      name: `About Zarolla — Classical Art Education from Master Artists | ${SITE_NAME}`,
      description: 'Zarolla Online Art Academy brings classical art training to students worldwide.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      mainEntity: { '@id': `${SITE_URL}/#organization` },
      datePublished: '2007-01-01',
      dateModified: new Date().toISOString().split('T')[0],
      author: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'en-US',
    }}
  />
);

export const MissionPageSEO = () => (
  <SEO
    title="Our Mission"
    description="Preserving and teaching classical art techniques for the next generation. Our mission is to make professional art education accessible to aspiring artists worldwide."
    url="/mission"
    keywords={['art education mission', 'classical art training', 'art academy']}
    datePublished="2007-01-01"
    dateModified={new Date().toISOString().split('T')[0]}
  />
);

export const LoginPageSEO = () => (
  <SEO
    title="Sign In"
    description="Sign in to access your Zarolla art courses, workshops, and video library. Continue your artistic journey with professional instruction."
    url="/login"
    noindex={true}
  />
);

export const ContactPageSEO = () => (
  <SEO
    title="Contact Zarolla — Support & Inquiries"
    description="Get in touch with Zarolla Online Art Academy. Questions about courses, video access, technical support, or partnership inquiries — our team typically responds within one business day."
    url="/contact"
    keywords={['contact Zarolla', 'art course support', 'online art academy help']}
  />
);

export default SEO;
